import React from 'react';
import { Link } from 'react-router-dom';
import { SocialProofBar } from '../components/SocialProofBar';
import { LeadForm } from '../components/LeadForm';
import { TrustBadges } from '../components/TrustBadges';
import { ProgramCard } from '../components/ProgramCard';
import { BenefitCard } from '../components/BenefitCard';
import { ReviewCard } from '../components/ReviewCard';
import { CoachCard } from '../components/CoachCard';
import { ScheduleTable } from '../components/ScheduleTable';
import { LocationBlock } from '../components/LocationBlock';
import { FAQAccordion } from '../components/FAQAccordion';
import { CTABand } from '../components/CTABand';
import { Shield, Zap, Target, Users, Heart, Trophy } from 'lucide-react';
export function Home() {
  return <div className="flex flex-col" data-id="element-539">
      {/* Hero Section */}
      <section className="relative bg-ink pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden" data-id="element-540">
        <div className="absolute inset-0 opacity-20" data-id="element-541">
          <img src="https://images.unsplash.com/photo-1599058917212-d750089bc07e?auto=format&fit=crop&w=2000&q=80" alt="Taekwondo Background" className="w-full h-full object-cover" data-id="element-542" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-transparent" data-id="element-543" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-544">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-545">
            <div className="flex flex-col items-start" data-id="element-546">
              <div className="inline-block bg-storm-red text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide mb-6" data-id="element-547">
                Free One-Week Trial for New Students
              </div>
              <div className="mb-8" data-id="element-548">
                <SocialProofBar data-id="element-549" />
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl text-white mb-6 leading-[1.1]" data-id="element-550">
                Build Confidence, Focus & Discipline at Storm Taekwondo
              </h1>
              <p className="text-xl text-off-white mb-8 max-w-xl leading-relaxed" data-id="element-551">
                Try one week free at Storm Taekwondo and experience a
                structured, supportive martial arts program that helps students
                build confidence, discipline, focus, fitness and self-defense
                skills.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-10" data-id="element-552">
                <Link to="/free-trial" className="btn-primary" data-id="element-553">
                  Claim Your Free Week
                </Link>
                <a href="tel:517-220-0621" className="btn-secondary" data-id="element-554">
                  Call 517-220-0621
                </a>
              </div>
              <TrustBadges data-id="element-555" />
            </div>

            <div className="lg:pl-10 hidden md:block" data-id="element-556">
              <LeadForm data-id="element-557" />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-24 bg-off-white" data-id="element-558">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-559">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-560">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-561">
              Find the Right Storm Program
            </h2>
            <p className="text-xl text-gray-warm" data-id="element-562">
              Structured classes for all ages and experience levels. No prior
              martial arts experience needed to start.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-563">
            <ProgramCard title="Mini Troopers" ageRange="Ages 3-5" benefit="Build Focus & Coordination" whoItsFor="Perfect for active preschoolers who need a positive outlet for their energy." imageUrl="https://drwzpk38qkpfb.cloudfront.net/www.stormtaekwondo.com/uploaded/images/Redesigne%202024/Mini-Troopers-Storm-Taekwondo.jpg" href="/mini-troopers" data-id="element-564" />
            <ProgramCard title="Kids Taekwondo" ageRange="Ages 6+" benefit="Develop Confidence & Discipline" whoItsFor="Structured classes that teach respect, goal setting, and self-defense fundamentals." imageUrl="https://drwzpk38qkpfb.cloudfront.net/www.stormtaekwondo.com/uploaded/images/Redesigne%202024/Discover%20the%20Perfect%20Program%20for%20Your%20Mini%20Trooper.png" href="/kids-taekwondo-classes" data-id="element-565" />
            <ProgramCard title="Family Classes" ageRange="All Ages" benefit="Train & Grow Together" whoItsFor="Families looking for a healthy, active hobby they can do on the same mat." imageUrl="https://cdn.magicpatterns.com/uploads/jpxHBCPtqv9Pv8vdpeiuPD/Family.png" href="/family-martial-arts-classes" data-id="element-566" />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-paper border-y border-subtle-gray" data-id="element-567">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-568">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-569">
            <h2 className="text-4xl md:text-5xl text-ink mb-6" data-id="element-570">
              More Than Kicks - Life Skills That Carry Off the Mat
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-571">
            <BenefitCard icon={Shield} title="Confidence" description="Students learn to stand tall, speak clearly, and believe in their ability to overcome challenges." data-id="element-572" />
            <BenefitCard icon={Target} title="Focus" description="Structured drills improve attention span and concentration, which carries over to school and work." data-id="element-573" />
            <BenefitCard icon={Zap} title="Discipline" description="We teach the importance of doing the right thing, even when it's hard, building strong character." data-id="element-574" />
            <BenefitCard icon={Heart} title="Fitness" description="Dynamic, full-body workouts that improve coordination, strength, flexibility, and cardiovascular health." data-id="element-575" />
            <BenefitCard icon={Users} title="Teamwork" description="A supportive environment where students encourage each other and build lasting friendships." data-id="element-576" />
            <BenefitCard icon={Trophy} title="Goal Setting" description="The belt system provides clear, achievable goals that teach the value of long-term dedication." data-id="element-577" />
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 bg-off-white" data-id="element-578">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-579">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-580">
            What Families Say About Storm Taekwondo
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-581">
            <ReviewCard quote="My son has been going here for 6 months and his confidence has skyrocketed. The coaches are amazing with the kids." name="Sarah M." label="Parent Review" data-id="element-582" />
            <ReviewCard quote="Great family atmosphere. We started in the family class and it's been the best decision we've made for our health." name="David R." label="Parent Review" data-id="element-583" />
            <ReviewCard quote="The discipline and focus my daughter has learned here translates directly to her schoolwork. Highly recommend Storm!" name="Jennifer L." label="Parent Review" data-id="element-584" />
          </div>
        </div>
      </section>

      {/* Schedule & Location */}
      <section className="py-24 bg-paper border-t border-subtle-gray" data-id="element-585">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-586">
          <div className="grid lg:grid-cols-2 gap-16" data-id="element-587">
            <div data-id="element-588">
              <h2 className="text-4xl text-ink mb-8" data-id="element-589">Class Times</h2>
              <ScheduleTable data-id="element-590" />
            </div>
            <div data-id="element-591">
              <h2 className="text-4xl text-ink mb-8" data-id="element-592">
                Taekwondo Classes in Farmington Hills, MI
              </h2>
              <LocationBlock data-id="element-593" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-off-white" data-id="element-594">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-595">
          <h2 className="text-4xl md:text-5xl text-ink text-center mb-16" data-id="element-596">
            Questions Before Your Free Week?
          </h2>
          <FAQAccordion faqs={[{
          question: 'How does the free one-week trial work?',
          answer: "Simply fill out the form, and we'll contact you to schedule your first visit. You'll get to try our classes for a full week with no obligation to join."
        }, {
          question: 'What age can my child start?',
          answer: 'We accept students as young as 3 years old in our Mini Troopers program, which is specifically designed for preschool-aged children.'
        }, {
          question: 'Does my child need experience?',
          answer: 'Not at all! The vast majority of our new students have no prior martial arts experience. Our beginner classes are designed to teach the fundamentals from day one.'
        }, {
          question: 'Do parents need to buy gear before the trial?',
          answer: 'No gear is needed to start. Just wear comfortable athletic clothing for your trial classes.'
        }]} data-id="element-597" />
        </div>
      </section>

      {/* Final CTA */}
      <CTABand headline="Ready to Try Storm Taekwondo for Free?" copy="Give your child, yourself or your family a chance to experience Storm before committing. Claim a free week and see why local families choose Storm Taekwondo." data-id="element-598" />
    </div>;
}